import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';

import { IconLibrary } from '@finnairoyj/fcom-ui-styles';

import { FinnairThemeService } from '@fcom/common/services/theme/finnair-theme.service';
import { FinnairTheme } from '@fcom/common/interfaces/theme.interface';

import { type IconSet } from '../../icons';
import { type AriaOptions } from '../../interfaces';
import { IconButtonSize, IconButtonTheme } from './enums';
import { ButtonType } from '../button';

@Component({
  selector: 'fcom-icon-button',
  templateUrl: './icon-button.component.html',
  styleUrls: ['./icon-button.component.scss', '../buttons-shared-styles.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class IconButtonComponent {
  private themeService = inject(FinnairThemeService);

  readonly IconLibrary = IconLibrary;

  /**
   * The size of the button, currently 3 static sizes
   */
  size = input<IconButtonSize>(IconButtonSize.MEDIUM);

  /**
   * There are 8 themes available right now that renders different backgrounds, font colors and borders
   */
  theme = input<IconButtonTheme>(IconButtonTheme.LIGHT_PRIMARY_NORDIC_BLUE);

  /**
   * Theme checker used for fcom-icon category rendering
   */
  isNewBrand = computed((): boolean => this.themeService.siteTheme() !== FinnairTheme.LEGACY);

  computedTheme = computed((): IconButtonTheme => {
    const siteTheme: FinnairTheme = this.themeService.siteTheme();

    if (siteTheme === FinnairTheme.LEGACY) {
      return this.theme();
    }

    switch (this.theme()) {
      case IconButtonTheme.LIGHT_PRIMARY_CTA_HEATHER:
      case IconButtonTheme.DARK_PRIMARY_CTA_HEATHER:
      case IconButtonTheme.PRIMARY_ACCENT:
        return IconButtonTheme.PRIMARY_ACCENT;
      case IconButtonTheme.DARK_SECONDARY_OUTLINE:
      case IconButtonTheme.SECONDARY_OUTLINE_REVERSED:
        return IconButtonTheme.SECONDARY_OUTLINE_REVERSED;
      case IconButtonTheme.LIGHT_TERTIARY_GHOST:
      case IconButtonTheme.LIGHT_TERTIARY_FORM:
      case IconButtonTheme.TERTIARY_GHOST:
        return IconButtonTheme.TERTIARY_GHOST;
      case IconButtonTheme.DARK_TERTIARY_GHOST:
      case IconButtonTheme.DARK_TERTIARY_FORM:
      case IconButtonTheme.TERTIARY_GHOST_REVERSED:
        return IconButtonTheme.TERTIARY_GHOST_REVERSED;
      default:
        return IconButtonTheme.SECONDARY_OUTLINE;
    }
  });

  /**
   * An icon to include, with its category and name
   */
  icon = input.required<IconSet>();

  /**
   * The aria tags that must be added to a button or link
   */
  aria = input.required<AriaOptions>();

  /**
   * Whether the button should be disabled or not
   */
  disabled = input<boolean>(false);

  /**
   * The pre-translated hover text to display
   */
  title = input<string | undefined>(undefined);

  /**
   * If button should render as an a tag, then pass in the url
   */
  href = input<string | undefined>(undefined);

  /**
   * If link, should it open in a new tab
   */
  openInNewTab = input<boolean | undefined>(undefined);

  /**
   * If link, is it external
   */
  externalLink = input<boolean | undefined>(undefined);

  /**
   * The type of button, for example "button" or "submit"
   */
  type = input<ButtonType>(ButtonType.BUTTON);

  clickBtn = output<Event>();

  /**
   * A data-testid that is passed on to the `<button>`
   */
  dataTestId = input<string | undefined>(undefined);

  public readonly IconButtonSize = IconButtonSize;
  public readonly IconButtonTheme = IconButtonTheme;

  onClick(event: Event): void {
    if (this.disabled() || this.aria().disabled) {
      event.preventDefault();
      return;
    }

    this.clickBtn.emit(event);
  }
}
